<template>
  <div class="preset-selector preset-selector--mockup" @click="handleSelect">
    <HorizontalScroller class="preset-selector__items">
      <div
        v-for="sizePreset in sizePresets"
        :key="sizePreset.id"
        class="preset-selector__item"
        :data-preset="sizePreset.id"
        :data-is-valid="sizePreset.isValid"
        :class="{
          'preset-selector__item--selected': isSelected(sizePreset.id),
          'preset-selector__item--invalid': !sizePreset.isValid,
          'preset-selector__item--standard-artwork': isStandardArtwork
        }"
      >
        <ShirtMockup
          :artwork-url="artworkUrl"
          :preset="sizePreset.id"
          :selected-color="selectedColor"
        />
        <div class="preset-selector__title">{{ sizePreset.title }}</div>
      </div>
    </HorizontalScroller>
    <div v-if="hasInvalidPresets" class="preset-selector__error-info">
      *Some sizes are unavailable. Minimum size is 1 inch.
    </div>
    <PresetMockupColorPicker :index="index" />
  </div>
</template>

<script>
import { mapState } from "pinia";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import PresetMockupColorPicker from "./PresetMockupColorPicker.vue";
import { MAX_PRINT_SIZES_GROUPED } from "../common/constants";
import HorizontalScroller from "../common/HorizontalScroller.vue";
import ShirtMockup from "./ShirtMockup.vue";
import { useDtfStore } from "../store";

export default {
  name: "PresetSelector",
  components: {
    ShirtMockup,
    HorizontalScroller,
    PresetMockupColorPicker
  },
  props: {
    sizePresets: {
      type: Array,
      default: () => []
    },
    selectedPresets: {
      type: Array,
      default: () => ["custom"]
    },
    disabled: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      default: 0
    }
  },
  emits: ["select"],
  computed: {
    ...mapState(useDtfStore, ["artworks", "isStandardArtwork", "presetMockupColor"]),
    artwork() {
      return this.artworks[this.index];
    },
    artworkUrl() {
      return this.artwork.manualEditUrl || this.artwork.url;
    },
    hasInvalidPresets() {
      return this.sizePresets.length < Object.keys(MAX_PRINT_SIZES_GROUPED).length;
    },
    selectedColor() {
      return this.presetMockupColor[this.index];
    }
  },
  methods: {
    isSelected(preset) {
      return this.selectedPresets.includes(preset);
    },
    isLastPreset(preset) {
      return this.selectedPresets.length === 1 && this.selectedPresets[0] === preset;
    },
    submitEvent(preset) {
      const action = this.isSelected(preset) ? "deselected" : "selected";
      submitDtfEvent("common_size_pressed", { sizeName: preset, action });
    },
    handleSelect(e) {
      if (this.disabled) return;

      const { preset, isValid } = e.target.dataset;

      if (this.isLastPreset(preset) || !isValid) return;

      this.submitEvent(preset);

      this.$emit("select", {
        preset,
        sizes: MAX_PRINT_SIZES_GROUPED[preset]
      });

      const container = e.target.closest(".preset-selector__items");
      if (container) {
        container.scrollLeft = e.target.offsetLeft;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.preset-selector {
  $this: &;

  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;

  &--mockup {
    #{$this}__item {
      height: 142px;
      width: 120px;

      @include with-desktop {
        height: 142px;
      }
    }
  }

  &__items {
    padding-top: 3px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
    flex-shrink: 0;
    width: 90px;
    height: 111px;
    padding: 12px;
    gap: 4px;
    border-radius: 8px;
    border: 2px solid $app-gray-modern-300;
    background-color: $white;
    cursor: pointer;
    position: relative;
    margin-right: 10px;

    &:hover {
      border-color: $app-gray-modern-500;
    }

    &::before {
      content: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEwIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik05IDFMMy41IDYuNUwxIDQiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=);
      background-color: $primary-600;
      height: 20px;
      width: 20px;
      position: absolute;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      top: -5px;
      right: -8px;
      display: none;
      z-index: 10;
    }

    &--standard-artwork {
      &::before {
        background-color: $primary-light;
      }
    }

    @include with-desktop {
      width: 106px;
      height: 112px;
    }

    &--invalid {
      pointer-events: none;
      cursor: not-allowed;
      display: none;
    }

    &--selected {
      border: 2px solid $primary-600;

      &:hover {
        border-color: $primary-600;
      }

      &::before {
        display: flex;
      }
    }

    &--selected#{&}--standard-artwork {
      border-color: $primary-light;
    }
  }

  &__image {
    width: min-content;
    pointer-events: none;
    flex-shrink: 0;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    line-height: 16.1px;
    text-align: center;
    margin-bottom: 0;
    color: $app-gray-modern-600;
    white-space: nowrap;
    pointer-events: none;
  }

  &__error-info {
    font-size: 12px;
    font-weight: 400;
    line-height: 13.8px;
    text-align: left;
    color: $app-gray-modern-500;
  }
}
</style>
